'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

type CheckInStep = 'promise' | 'failure' | 'correction' | 'complete';

const failureReasons = [
  { id: 'reason-hunger', label: 'Hunger', icon: 'FireIcon' },
  { id: 'reason-stress', label: 'Stress', icon: 'BoltIcon' },
  { id: 'reason-planning', label: 'Poor Planning', icon: 'ClipboardDocumentListIcon' },
  { id: 'reason-emotional', label: 'Emotional Eating', icon: 'HeartIcon' },
  { id: 'reason-alcohol', label: 'Alcohol', icon: 'BeakerIcon' },
  { id: 'reason-social', label: 'Social Event', icon: 'UserGroupIcon' },
  { id: 'reason-fatigue', label: 'Fatigue', icon: 'MoonIcon' },
  { id: 'reason-lazy', label: 'Laziness', icon: 'FaceFrownIcon' },
  { id: 'reason-other', label: 'Other', icon: 'EllipsisHorizontalCircleIcon' },
];

interface FailureFormData {
  correction: string;
}

export default function PromiseCard() {
  const [step, setStep] = useState<CheckInStep>('promise');
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FailureFormData>();

  function handleKept() {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStep('complete');
      // BACKEND: POST /api/check-ins — { profile: 'theo', date: today, kept: true }
      toast.success('Promise kept. The forge holds.', {
        description: 'Streak extended to 15 days.',
      });
    }, 800);
  }

  function handleBroken() {
    setStep('failure');
  }

  function handleReasonSelected(reasonId: string) {
    setSelectedReason(reasonId);
    setStep('correction');
  }

  function onSubmitCorrection(data: FailureFormData) {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStep('complete');
      // BACKEND: POST /api/check-ins — { profile: 'theo', date: today, kept: false, reason: selectedReason, correction: data.correction }
      toast('Check-in recorded.', {
        description: 'Tomorrow is the correction.',
      });
    }, 800);
  }

  return (
    <div className="card-forge rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-forge-label mb-1">Daily Check-In</div>
            <div className="text-lg font-black text-foreground">Monday, 08 June 2026</div>
          </div>
          <div className="text-right">
            <div className="text-forge-label">Today&apos;s Score</div>
            <div className="font-tabular font-black text-3xl text-primary">87</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Step: Promise */}
        {step === 'promise' && (
          <div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center mx-auto mb-4">
                <Icon name="ShieldCheckIcon" size={28} className="text-primary" />
              </div>
              <h2 className="text-xl font-black text-foreground mb-2">
                Did I keep the promise today?
              </h2>
              <p className="text-sm text-muted-foreground">
                Compliance is the scoreboard. Answer honestly.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={handleKept}
                disabled={isSubmitting}
                className="group relative py-8 rounded-xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-all duration-200 active:scale-95 disabled:opacity-50"
              >
                <div className="flex flex-col items-center gap-3">
                  <Icon name="CheckCircleIcon" size={36} className="text-primary" />
                  <div>
                    <div className="text-xl font-black text-primary tracking-wider">YES</div>
                    <div className="text-xs text-muted-foreground">Promise kept</div>
                  </div>
                </div>
                {isSubmitting && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-xl">
                    <Icon name="ArrowPathIcon" size={20} className="text-primary animate-spin" />
                  </div>
                )}
              </button>

              <button
                onClick={handleBroken}
                className="group py-8 rounded-xl border-2 border-destructive/30 bg-destructive/5 hover:bg-destructive/10 hover:border-destructive/50 transition-all duration-200 active:scale-95"
              >
                <div className="flex flex-col items-center gap-3">
                  <Icon name="XCircleIcon" size={36} className="text-destructive" />
                  <div>
                    <div className="text-xl font-black text-destructive tracking-wider">NO</div>
                    <div className="text-xs text-muted-foreground">Promise broken</div>
                  </div>
                </div>
              </button>
            </div>

            <p className="text-center text-2xs text-muted-foreground mt-6 italic">
              &ldquo;The forge reveals the truth.&rdquo;
            </p>
          </div>
        )}

        {/* Step: Failure reason */}
        {step === 'failure' && (
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="ExclamationTriangleIcon" size={20} className="text-destructive" />
                <h2 className="text-lg font-black text-foreground">What broke today?</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Name it. Own it. Fix it tomorrow.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {failureReasons.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => handleReasonSelected(reason.id)}
                  className="p-3 rounded-lg border border-border hover:border-destructive/40 hover:bg-destructive/5 transition-all duration-150 active:scale-95 text-center"
                >
                  <Icon
                    name={reason.icon as Parameters<typeof Icon>[0]['name']}
                    size={20}
                    className="text-muted-foreground mx-auto mb-1"
                  />
                  <div className="text-xs font-medium text-foreground">{reason.label}</div>
                </button>
              ))}
            </div>

            <button
              onClick={() => setStep('promise')}
              className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Icon name="ArrowLeftIcon" size={12} />
              Back
            </button>
          </div>
        )}

        {/* Step: Correction */}
        {step === 'correction' && (
          <form onSubmit={handleSubmit(onSubmitCorrection)}>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="ArrowPathIcon" size={20} className="text-accent" />
                <h2 className="text-lg font-black text-foreground">What is tomorrow&apos;s correction?</h2>
              </div>
              {selectedReason && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-destructive/10 border border-destructive/30 text-xs text-destructive font-bold mb-3">
                  <Icon name="ExclamationCircleIcon" size={12} />
                  {failureReasons.find((r) => r.id === selectedReason)?.label}
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                One specific action. No second bad day.
              </p>
            </div>

            <div className="space-y-2 mb-6">
              <label className="text-forge-label block">Tomorrow&apos;s Correction Plan</label>
              <p className="text-2xs text-muted-foreground">Be specific — a vague plan is no plan.</p>
              <textarea
                {...register('correction', {
                  required: 'Describe your correction — no vague answers.',
                  minLength: { value: 10, message: 'Be more specific — at least 10 characters.' },
                })}
                rows={4}
                placeholder="e.g. Prep meals tonight. No negotiating at lunch. Walk 12k steps."
                className="w-full bg-input border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors resize-none"
              />
              {errors.correction && (
                <p className="text-xs text-destructive">{errors.correction.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setStep('failure')}
                className="btn-forge-ghost py-3 rounded-lg text-sm"
              >
                ← BACK
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-forge-primary py-3 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    SAVING...
                  </>
                ) : (
                  'COMMIT →'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Step: Complete */}
        {step === 'complete' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/40 flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckBadgeIcon" size={32} className="text-primary" />
            </div>
            <h2 className="text-xl font-black text-foreground mb-2">Check-In Complete</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Recorded for 08 June 2026.
            </p>
            <div className="card-elevated rounded-lg p-4 border border-primary/20 mb-6">
              <div className="text-forge-label mb-1">Today&apos;s Forge Score</div>
              <div className="font-tabular font-black text-4xl text-primary">87</div>
              <div className="text-xs text-muted-foreground mt-1">Streak: 14 days</div>
            </div>
            <p className="text-sm text-muted-foreground italic">
              &ldquo;Execution beats motivation.&rdquo;
            </p>
            <button
              onClick={() => setStep('promise')}
              className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors underline"
            >
              Reset check-in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}